import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/appContext";
import { AppLayout } from "../../../containers/layout/app";
import { AuditTrailWrapper } from "./styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import SearchIcon from "@mui/icons-material/Search";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import {
	Box,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { AuditTrailTable } from "../../../containers/tables/audittrailtable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAuditsService } from "../../../utils/api/report/audittrail/retrieveAudits";
import { BaseButton } from "../../../components/button/styled";

export const AuditTrail = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const filterOptions = [
		{
			id: "Password Reset",
			title: "Password Reset Operations",
		},
		{
			id: "Profile Update",
			title: "Profile Update Operations",
		},
	];

	const { setActiveNavItem } = useContext(AppContext);

	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});
	const [formDetails, setFormDetails] = useState({
		user: "",
		action: " ",
		startDate: "",
		endDate: "",
	});

	useEffect(() => {
		setActiveNavItem("Report");
	}, [setActiveNavItem]);

	const { data: audits } = useQuery({
		queryKey: ["audits", TOKEN, formDetails, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAuditsService(
				TOKEN,
				formDetails,
				paginationIndex,
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout pageId={"Audit-Trail"} pageTitle={"Audit Trail"}>
			<AuditTrailWrapper>
				<form className="filter-bar">
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">User</BaseLabel>
						<BaseInput
							name="user"
							fontweight={400}
							value={formDetails.user}
							placeholder="Search Username"
							endAdornment={
								<SearchIcon style={{ height: "0.75em", width: "auto" }} />
							}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Action</BaseLabel>
						<BaseSelect
							name="action"
							fontweight={400}
							value={formDetails.action}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Filter by Action</BaseOption>
							{filterOptions?.map(
								(option: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={option.id}>
											{option.title}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Choose Date Range</BaseLabel>
						<Stack
							direction={"row"}
							gap={"calc(var(--flex-gap)/8)"}
							flexWrap={"wrap"}
						>
							<Box flex={1}>
								<BaseInput
									type="date"
									name="startDate"
									fontweight={400}
									value={formDetails.startDate}
									colour="var(--semi-gray-color)"
									sx={{ width: "-webkit-fill-available" }}
									onChange={(e) => handleChange(e)}
								/>
							</Box>
							<Box flex={1}>
								<BaseInput
									type="date"
									name="endDate"
									fontweight={400}
									value={formDetails.endDate}
									colour="var(--semi-gray-color)"
									sx={{ width: "-webkit-fill-available" }}
									onChange={(e) => handleChange(e)}
								/>
							</Box>
						</Stack>
					</BaseFieldSet>
				</form>
				<Box component={"div"} className="audit-trail-table-box">
					<AuditTrailTable rows={audits as Record<string, any>[]} />
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</AuditTrailWrapper>
		</AppLayout>
	);
};
